import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Play, Pause, Volume2, Clock, Heart, Moon, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const LullabySection = () => {
  const { t } = useTranslation();
  const [lullaby, setLullaby] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.6);
  const [loading, setLoading] = useState(true);
  const audioRef = useRef(null);

  // Fetch lullabies on component mount
  useEffect(() => {
    fetchLullaby();
  }, []);

  // Audio event listeners
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleLoadedMetadata = () => setDuration(audio.duration);
    const handleEnded = () => {
      setIsPlaying(false);
      trackLullabyEvent('complete', Math.floor(audio.currentTime));
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [lullaby]);

  const fetchLullaby = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/lullabies`);
      const data = await response.json();
      if (data.length > 0) {
        setLullaby(data[0]); // Set the single lullaby
      }
    } catch (error) {
      console.error('Error fetching lullaby:', error);
    } finally {
      setLoading(false);
    }
  };

  const trackLullabyEvent = async (eventType, durationListened = 0) => {
    if (!lullaby) return;
    
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/track-lullaby`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lullaby_id: lullaby.id,
          event_type: eventType,
          duration_listened: durationListened,
          user_agent: navigator.userAgent,
        }),
      });
    } catch (error) {
      console.error('Error tracking lullaby:', error);
    }
  };

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio || !lullaby) return;

    if (isPlaying) {
      audio.pause();
      trackLullabyEvent('pause', Math.floor(audio.currentTime));
    } else {
      audio.play();
      trackLullabyEvent('play', Math.floor(audio.currentTime));
    }
    setIsPlaying(!isPlaying);
  };

  const handleVolumeChange = (newVolume) => {
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
  };

  const handleSeek = (newTime) => {
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const formatTime = (timeInSeconds) => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'nature': return '🌿';
      case 'classic': return '🎼';
      case 'modern': return '✨';
      default: return '🎵';
    }
  };

  if (loading) {
    return (
      <section className="py-20 bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
            <p className="text-gray-600">{t('lullaby.loading')}</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-200/30 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/3 right-1/3 w-80 h-80 bg-purple-200/30 rounded-full blur-3xl animate-pulse" style={{animationDelay: '2s'}}></div>
        <div className="absolute top-1/2 right-1/4 w-64 h-64 bg-pink-200/20 rounded-full blur-3xl animate-pulse" style={{animationDelay: '4s'}}></div>
      </div>

      <div className="max-w-4xl mx-auto px-6 relative z-10">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="flex items-center justify-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-r from-purple-400 to-pink-400 rounded-2xl flex items-center justify-center shadow-lg">
              <Moon className="w-8 h-8 text-white" />
            </div>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-black text-gray-800 mb-6">
            {t('lullaby.title')}
          </h2>
          
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            {t('lullaby.subtitle')}
          </p>
        </motion.div>

        {/* Lullaby Player */}
        {lullaby && (
          <motion.div
            className="bg-white/80 backdrop-blur-md rounded-3xl border border-white/50 shadow-2xl p-8 mb-8"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            {/* Audio element */}
            <audio
              ref={audioRef}
              src={lullaby.audio_url}
              onLoadedData={() => setDuration(audioRef.current.duration)}
              volume={volume}
            />

            {/* Lullaby Info */}
            <div className="text-center mb-8">
              <div className="flex items-center justify-center mb-4">
                <div>
                  <h3 className="text-2xl font-bold text-gray-800">{lullaby.title}</h3>
                </div>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="mb-6">
              <div className="flex items-center justify-between text-sm text-gray-500 mb-2">
                <span>{formatTime(currentTime)}</span>
                <span>{formatTime(duration)}</span>
              </div>
              <div
                className="w-full bg-gray-200 rounded-full h-2 cursor-pointer"
                onClick={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const x = e.clientX - rect.left;
                  const newTime = (x / rect.width) * duration;
                  handleSeek(newTime);
                }}
              >
                <div
                  className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%` }}
                />
              </div>
            </div>

            {/* Controls */}
            <div className="flex items-center justify-center space-x-6">
              {/* Play/Pause Button */}
              <motion.button
                onClick={togglePlay}
                className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white rounded-full flex items-center justify-center shadow-lg"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {isPlaying ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6 ml-1" />}
              </motion.button>

              {/* Volume Control */}
              <div className="flex items-center space-x-3">
                <Volume2 className="w-5 h-5 text-gray-600" />
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={volume}
                  onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
                  className="w-24 accent-purple-500"
                />
              </div>

              {/* Duration Display */}
              <div className="flex items-center space-x-2 text-gray-600">
                <Clock className="w-4 h-4" />
                <span className="text-sm">{Math.floor(duration / 60)} min</span>
              </div>
            </div>
          </motion.div>
        )}

        {/* Benefits Section */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-3 gap-8"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
        >
          <div className="text-center">
            <div className="w-12 h-12 bg-gradient-to-r from-blue-400 to-blue-500 rounded-xl flex items-center justify-center mx-auto mb-4">
              <Heart className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-lg font-bold text-gray-800 mb-2">{t('lullaby.benefits.calm.title')}</h3>
            <p className="text-gray-600 text-sm">{t('lullaby.benefits.calm.description')}</p>
          </div>

          <div className="text-center">
            <div className="w-12 h-12 bg-gradient-to-r from-purple-400 to-purple-500 rounded-xl flex items-center justify-center mx-auto mb-4">
              <Moon className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-lg font-bold text-gray-800 mb-2">{t('lullaby.benefits.sleep.title')}</h3>
            <p className="text-gray-600 text-sm">{t('lullaby.benefits.sleep.description')}</p>
          </div>

          <div className="text-center">
            <div className="w-12 h-12 bg-gradient-to-r from-pink-400 to-pink-500 rounded-xl flex items-center justify-center mx-auto mb-4">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-lg font-bold text-gray-800 mb-2">{t('lullaby.benefits.connection.title')}</h3>
            <p className="text-gray-600 text-sm">{t('lullaby.benefits.connection.description')}</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default LullabySection;